import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import './Launch.css';
import { resolveLaunchContinue } from '../../services/advertiserOutreach';
import { useLegalDocument } from '../../hooks/useLegalDocument';

const CONTINUE_META_KEY = 'launch_continue_meta';

export default function LaunchContinue() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [checkoutData, setCheckoutData] = useState(null);
    const [isPaying, setIsPaying] = useState(false);
    const [tosAccepted, setTosAccepted] = useState(false);
    const [checkoutTosError, setCheckoutTosError] = useState('');

    const { doc: tosDoc } = useLegalDocument('advertiser_tos');
    const tosVersion = tosDoc?.version;

    useEffect(() => {
        if (!token) {
            setError('This payment link is invalid or incomplete.');
            setLoading(false);
            return;
        }

        let cancelled = false;
        resolveLaunchContinue(token)
            .then((data) => {
                if (!cancelled) {
                    setCheckoutData(data);
                    setError('');
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err.message || 'Could not load your campaign');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [token]);

    const handleStripeCheckout = async () => {
        if (!tosAccepted) {
            setCheckoutTosError('You must agree to the Terms of Service before proceeding to payment.');
            return;
        }
        setCheckoutTosError('');
        setIsPaying(true);
        setError('');

        try {
            sessionStorage.setItem(
                CONTINUE_META_KEY,
                JSON.stringify({
                    continue: true,
                    campaignName: checkoutData.campaignName,
                    advertisementId: checkoutData.advertisementId,
                })
            );

            const response = await fetch(`${process.env.REACT_APP_API_URL}/launch/checkout/create-stripe-session`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    continueToken: checkoutData.continueToken || token,
                }),
            });

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.error || 'Failed to start checkout');
            }

            const data = await response.json();
            if (data.url) {
                window.location.href = data.url;
            } else {
                throw new Error('Checkout URL missing from server');
            }
        } catch (err) {
            setError(err.message || 'Checkout failed');
            setIsPaying(false);
        }
    };

    if (loading) {
        return (
            <div className="launch-container">
                <div className="launch-content">
                    <p>Loading your campaign…</p>
                </div>
            </div>
        );
    }

    if (error && !checkoutData) {
        return (
            <div className="launch-container">
                <div className="launch-content launch-step">
                    <h1>Unable to continue</h1>
                    <p className="launch-error-text">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="launch-container">
            <div className="launch-content">
                <div className="launch-step">
                    <h1>Complete your ad</h1>
                    <h2>{checkoutData.campaignName}</h2>

                    {checkoutData.imageUrl && (
                        <div className="launch-preview-container launch-continue-preview">
                            <div className="preview-card">
                                <img src={checkoutData.imageUrl} alt="Your ad creative" />
                                <p>Your ad</p>
                            </div>
                        </div>
                    )}

                    <div className="launch-checkout-summary">
                        <p>Amount: ${checkoutData.screenCost}</p>
                        <p className="launch-checkout-duration">
                            Includes one month of display on this screen after approval.
                        </p>
                    </div>

                    <label className="launch-checkbox launch-checkout-tos">
                        <input
                            type="checkbox"
                            checked={tosAccepted}
                            onChange={(e) => {
                                setTosAccepted(e.target.checked);
                                if (e.target.checked) {
                                    setCheckoutTosError('');
                                }
                            }}
                        />
                        <span>
                            I agree to the{' '}
                            <Link
                                to="/tos"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="launch-tos-link"
                                onClick={(e) => e.stopPropagation()}
                            >
                                INVERT Terms of Service
                            </Link>{' '}
                            {tosVersion ? `(version ${tosVersion})` : ''}.
                        </span>
                    </label>
                    {checkoutTosError && (
                        <p className="launch-error-text launch-checkout-tos-error">{checkoutTosError}</p>
                    )}

                    {error && <p className="launch-error-text">{error}</p>}

                    <div className="checkout-methods">
                        <div className="stripe-cta">
                            <button
                                type="button"
                                className="launch-btn checkout"
                                onClick={handleStripeCheckout}
                                disabled={isPaying}
                            >
                                {isPaying ? 'Processing…' : 'Pay now'}
                            </button>
                            <p className="stripe-cta-subtext">Secure card payment powered by Stripe.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
